import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import ActionButtons, { EditButton, DeleteButton } from '../../components/ActionButtons';
import ModalShell from '../../components/ModalShell';
import GeriButon from '../../components/GeriButon';
import { apiRequest } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import UsageInfo from '../../components/UsageInfo';
import EmptyState from '../../components/EmptyState';
import PageHeader from '../../components/PageHeader';

function FormHata({ mesaj }) {
  if (!mesaj) return null;
  return <p className="text-[10px] font-black text-rose-500 uppercase tracking-widest mt-2 flex items-center gap-1 animate-shake">⚠️ {mesaj}</p>;
}

const toTitleCase = (str) => str ? str.toLocaleLowerCase('tr-TR').split(' ').map(word => word.charAt(0).toLocaleUpperCase('tr-TR') + word.slice(1)).join(' ') : '';

function BankaTanimiForm({ kayit, odemeTipleri, onKaydet, onGeri, kaydediliyor, hata }) {
  const { personel } = useAuth();
  const yeni = !kayit;
  const [form, setForm] = useState({
    ad: kayit?.ad ?? '',
    odemeTip: kayit?.odemeTip ?? (odemeTipleri?.[0]?.ad || ''),
    hesapAdSoyad: kayit?.hesapAdSoyad ?? (personel?.otelAdi || ''),
    iban: kayit?.iban ?? '',
    sube: kayit?.sube ?? '',
    subeNo: kayit?.subeNo ?? ''
  });
  const [hatalar, setHatalar] = useState({});

  useEffect(() => {
    if (yeni && !form.odemeTip && odemeTipleri?.length > 0) {
      setForm(p => ({ ...p, odemeTip: odemeTipleri[0].ad }));
    }
  }, [odemeTipleri, yeni, form.odemeTip]);

  const set = (k, v) => { setForm(p => ({ ...p, [k]: v })); setHatalar(p => ({ ...p, [k]: undefined })); };
  const INP = (hata) => `w-full border-2 rounded-2xl px-5 py-3.5 text-sm font-bold text-gray-800 transition-[border-color,box-shadow,background-color] duration-200 bg-white focus:outline-none ${hata ? 'border-rose-400 bg-rose-50' : 'border-gray-100 focus:border-sky-400 focus:ring-4 focus:ring-sky-50 shadow-sm'}`;

  async function handleKaydet() {
    const h = {};
    if (!form.ad?.trim()) h.ad = 'Banka adı zorunludur.';
    if (!form.odemeTip) h.odemeTip = 'Ödeme tipi seçimi zorunludur.';
    if (Object.keys(h).length > 0) { setHatalar(h); return; }
    onKaydet({ ...(kayit ? { bankaTanimiId: kayit.bankaTanimiId } : {}), ...form });
  }

  return (
    <ModalShell
      icon={yeni ? '➕' : '✏️'}
      iconColor="from-sky-600 to-indigo-700"
      title={yeni ? 'Yeni Banka Hesabı' : `"${kayit.ad}" Düzenle`}
      subtitle="Ödeme alınan hesaplar ve iban yönetimi"
      onClose={onGeri}
      size="xl"
      footer={
        <>
          <button onClick={onGeri} className="px-8 py-3.5 rounded-2xl border-2 border-gray-100 text-xs font-black text-gray-400 hover:bg-gray-100 transition-all uppercase tracking-widest active:scale-95">İPTAL</button>
          <button onClick={handleKaydet} disabled={kaydediliyor}
            className="px-10 py-3.5 bg-gradient-to-r from-sky-600 to-indigo-700 hover:from-sky-700 hover:to-indigo-800 disabled:opacity-60 text-white rounded-2xl text-xs font-black shadow-xl shadow-sky-200 transition-all hover:-translate-y-1 active:translate-y-0 tracking-widest uppercase">
            {kaydediliyor ? '⌛ KAYDEDİLİYOR...' : '💾 HESABI SİSTEME KAYDET'}
          </button>
        </>
      }
    >
      <div className="space-y-8">
          {(hata || hatalar.genel) && <div className="bg-rose-50 border-2 border-rose-100 rounded-2xl px-5 py-4 text-xs text-rose-600 font-black uppercase tracking-widest leading-none animate-shake">⚠️ {hata || hatalar.genel}</div>}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div>
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] block mb-3 ml-1">Banka Adı *</label>
                <input value={form.ad} onChange={e => set('ad', toTitleCase(e.target.value))} className={INP(hatalar.ad)} placeholder="Örn: Ziraat Bankası, Qnb..." autoFocus />
                <FormHata mesaj={hatalar.ad} />
              </div>
              <div>
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] block mb-3 ml-1">Hesap Ödeme Tipi *</label>
                <div className="relative">
                  <select value={form.odemeTip} onChange={e => set('odemeTip', e.target.value)} className={INP(hatalar.odemeTip) + ' appearance-none cursor-pointer pr-12'}>
                    <option value="">Ödeme Tipi Seçin</option>
                    {odemeTipleri?.map(t => <option key={t.odemeTipiId} value={t.ad}>{t.ad}</option>)}
                  </select>
                  <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-gray-300 text-xs bg-gray-50/50 w-7 h-7 flex items-center justify-center rounded-lg border border-gray-100 shadow-inner">▼</span>
                </div>
                <FormHata mesaj={hatalar.odemeTip} />
              </div>
              <div>
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] block mb-3 ml-1">Hesap Sahibi (Unvan)</label>
                <input value={form.hesapAdSoyad} onChange={e => set('hesapAdSoyad', e.target.value)} className={INP(false)} placeholder="Otel Ticari Unvanı" />
              </div>
            </div>
            <div className="space-y-6">
              <div>
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] block mb-3 ml-1">IBAN Numarası</label>
                <input value={form.iban} onChange={e => set('iban', e.target.value.toUpperCase())} className={INP(false) + ' font-mono tracking-[0.1em] text-xs'} placeholder="TR00 0000..." maxLength={32} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] block mb-3 ml-1">Şube Adı</label>
                  <input value={form.sube} onChange={e => set('sube', toTitleCase(e.target.value))} className={INP(false)} placeholder="Merkez" />
                </div>
                <div>
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] block mb-3 ml-1">Şube Kodu</label>
                  <input value={form.subeNo} onChange={e => set('subeNo', e.target.value)} className={INP(false)} placeholder="0001" />
                </div>
              </div>
              <div className="bg-sky-50/50 rounded-[2rem] p-6 border-2 border-sky-100 border-dashed animate-fadeIn">
                 <p className="text-[8px] font-black text-sky-400 uppercase tracking-[0.2em] mb-4 leading-none">Kart Önizleme</p>
                 <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-sky-500 to-indigo-700 rounded-2xl flex items-center justify-center text-white text-base font-black shadow-lg shadow-sky-100">
                       {form.ad?.[0] || 'B'}
                    </div>
                    <div className="min-w-0">
                       <p className="font-black text-gray-800 text-xs tracking-tight truncate leading-none mb-1">{form.ad || 'Banka Adı'}</p>
                       <p className="text-[9px] font-black text-sky-500 tracking-widest">{form.odemeTip || 'Ödeme Tipi'}</p>
                    </div>
                 </div>
              </div>
            </div>
          </div>
      </div>
    </ModalShell>
  );
}

export default function BankaTanimlari() {
  const [liste, setListe] = useState([]);
  const [odemeTipleri, setOdemeTipleri] = useState([]);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [hata, setHata] = useState(null);
  const [view, setView] = useState('liste');
  const [secili, setSecili] = useState(null);
  const [silOnay, setSilOnay] = useState(null);
  const [kopyalandi, setKopyalandi] = useState(null);
  const [aramaMetni, setAramaMetni] = useState('');
  const [kaydediliyor, setKaydediliyor] = useState(false);

  const veriYukle = useCallback(async () => {
    setYukleniyor(true); setHata(null);
    try {
      const [banks, payments] = await Promise.all([apiRequest('bankatanimlari'), apiRequest('odemetipleri')]);
      setListe(banks || []); setOdemeTipleri(payments || []);
    } catch { setHata('Veriler yüklenemedi.'); }
    setYukleniyor(false);
  }, []);

  useEffect(() => { veriYukle(); }, [veriYukle]);

  async function kaydet(form) {
    setKaydediliyor(true); setHata(null);
    try {
      if (form.bankaTanimiId) await apiRequest('bankatanimlari', 'PUT', form);
      else await apiRequest('bankatanimlari', 'POST', form);
      await veriYukle();
      setView('liste'); setSecili(null);
    } catch (e) {
      setHata(e?.message || 'Hata oluştu.');
    }
    setKaydediliyor(false);
  }

  async function handleSil(id) {
    try { await apiRequest(`bankatanimlari/${id}`, 'DELETE'); setListe(p => p.filter(x => x.bankaTanimiId !== id)); setSilOnay(null); }
    catch { alert('Hata oluştu.'); }
  }

  function kopyalaIban(iban) {
    navigator.clipboard?.writeText(iban); setKopyalandi(iban);
    setTimeout(() => setKopyalandi(null), 1800);
  }

  const filtrelenmis = liste.filter(x => x.ad?.toLocaleLowerCase('tr-TR').includes(aramaMetni.toLocaleLowerCase('tr-TR')) || x.hesapAdSoyad?.toLocaleLowerCase('tr-TR').includes(aramaMetni.toLocaleLowerCase('tr-TR')));
  const ic = (tip) => liste.filter(x => x.odemeTip === tip).length;
  const isOpen = view === 'ekle' || view === 'duzenle';

  return (
    <div className="p-3 md:p-6 space-y-7 min-h-screen bg-gray-50/50 animate-fadeIn" style={{ transform: 'translateZ(0)' }}>
      <PageHeader
        icon="🏦"
        iconColor="from-sky-500 to-indigo-700"
        iconShadow="shadow-sky-200"
        title="Banka Tanımları"
        subtitle="Ödeme alınan hesaplar ve iban yönetimi"
      >
        <button onClick={() => { setSecili(null); setView('ekle'); }}
          className="group flex-shrink-0 flex items-center justify-center gap-3 bg-gradient-to-r from-sky-600 to-indigo-700 hover:from-sky-700 hover:to-indigo-800 text-white px-10 py-4 rounded-2xl text-[10px] font-black shadow-2xl shadow-sky-200 transition-[background-color,transform] duration-300 hover:-translate-y-1 active:translate-y-0 uppercase tracking-widest relative overflow-hidden">
          <span className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300"></span>
          <span className="relative text-xl transition-transform group-hover:rotate-90">＋</span>
          <span className="relative">YENİ BANKA HESABI EKLE</span>
        </button>
      </PageHeader>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'TOPLAM HESAP', value: liste.length, icon: '🏦', color: 'from-gray-700 to-gray-900 shadow-gray-200' },
          { label: 'HAVALE / EFT', value: ic('BANKA HAVALE') + ic('EFT') || ic('HAVALE'), icon: '🔄', color: 'from-sky-500 to-sky-700 shadow-sky-100' },
          { label: 'KREDİ KARTI / POS', value: ic('KREDİ KARTI'), icon: '💳', color: 'from-indigo-500 to-indigo-700 shadow-indigo-100' },
          { label: 'DİĞER HESAPLAR', value: liste.length - (ic('BANKA HAVALE') + ic('EFT') + ic('KREDİ KARTI') + ic('HAVALE')), icon: '🏛️', color: 'from-slate-500 to-slate-700 shadow-slate-100' },
        ].map(k => (
          <div key={k.label} className={`relative overflow-hidden bg-gradient-to-br ${k.color} rounded-[2rem] p-6 text-white shadow-xl group hover:shadow-2xl transition-[box-shadow,transform] duration-300 group`}>
             <div className="absolute -right-4 -bottom-4 text-7xl opacity-10 pointer-events-none group-hover:scale-125 transition-transform duration-500">{k.icon}</div>
             <p className="text-[10px] font-black uppercase tracking-widest opacity-70 mb-1 leading-none">{k.label}</p>
             <p className="text-2xl font-black tracking-tight leading-none">{k.value}</p>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-[2.5rem] border border-gray-200 shadow-sm overflow-hidden min-h-[400px]">
        <div className="px-6 md:px-8 py-5 border-b border-gray-100 bg-gray-50/30 flex flex-col md:flex-row items-center gap-6">
          <div className="relative flex-1 w-full">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">🔍</span>
            <input value={aramaMetni} onChange={e => setAramaMetni(e.target.value)} placeholder="Banka, unvan veya iban ara..."
              className="w-full pl-11 pr-4 py-3.5 text-sm border-2 border-gray-100 rounded-2xl focus:border-sky-400 focus:outline-none bg-white shadow-sm transition-all font-bold" />
          </div>
          <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-auto whitespace-nowrap">Sistemde toplam <b className="text-sky-600 text-xs">{liste.length}</b> kayıt tanımlı</p>
        </div>

        {/* Mobile View */}
        <div className="md:hidden p-4 space-y-8 leading-none">
          {filtrelenmis.length === 0 ? (
            <EmptyState icon="🏦" text="Kayıt Bulunamadı" />
          ) : filtrelenmis.map((item, idx) => (
            <div key={item.bankaTanimiId} className={`relative rounded-[2.5rem] p-8 text-white shadow-2xl bg-gradient-to-br ${['from-slate-700 to-slate-900 shadow-slate-200', 'from-sky-600 to-sky-800 shadow-sky-200', 'from-indigo-600 to-indigo-800 shadow-indigo-200', 'from-teal-600 to-teal-800 shadow-teal-200'][idx % 4]} overflow-hidden group/card transform-gpu transition-all duration-500 hover:shadow-2xl`}>
              <div className="absolute -right-8 -top-8 w-32 h-32 bg-white/10 rounded-full blur-[40px] group-hover/card:scale-150 transition-transform duration-700" />
              <div className="flex justify-between items-start mb-10">
                <div className="z-10 min-w-0 pr-4">
                  <p className="text-[9px] uppercase tracking-[0.2em] opacity-60 font-black mb-2 leading-none">BANKA / ÖDEME TİPİ</p>
                  <h3 className="text-xl font-black leading-none tracking-tighter uppercase truncate">{item.ad}</h3>
                  <span className="inline-block mt-3 bg-white/20 backdrop-blur-xl border border-white/20 px-3 py-1.5 rounded-xl text-[9px] font-black uppercase tracking-widest">{item.odemeTip || 'BELİRTİLMEMİŞ'}</span>
                </div>
                <div className="w-12 h-12 bg-white/10 backdrop-blur-md rounded-2xl flex items-center justify-center text-xl z-10 border border-white/10 shadow-inner font-black leading-none">{idx + 1}</div>
              </div>

              <div className="mb-10 z-10 relative">
                <p className="text-[9px] uppercase tracking-[0.2em] opacity-60 font-black mb-3 leading-none">IBAN NUMARASI</p>
                <div className="bg-black/20 backdrop-blur-xl rounded-2xl p-4 flex items-center justify-between border border-white/10 group-hover/card:border-white/30 transition-all shadow-inner">
                  <span className="font-mono text-[11px] font-black tracking-[0.05em] truncate mr-4">{item.iban || 'BİLGİ GİRİLMEMİŞ'}</span>
                  {item.iban && (
                    <button onClick={() => kopyalaIban(item.iban)} className="w-10 h-10 bg-white/20 hover:bg-white/40 rounded-xl flex items-center justify-center transition-all flex-shrink-0 active:scale-90">
                      {kopyalandi === item.iban ? '✅' : '📋'}
                    </button>
                  )}
                </div>
              </div>

              <div className="flex justify-between items-end z-10 relative mt-4">
                <div className="min-w-0 pr-4">
                  <p className="text-[9px] uppercase tracking-[0.2em] opacity-60 font-black mb-1 leading-none">HESAP SAHİBİ</p>
                  <p className="text-[11px] font-black uppercase tracking-tight truncate leading-none">{item.hesapAdSoyad || '—'}</p>
                </div>
                <div className="flex gap-3 flex-shrink-0">
                  <EditButton size="lg" onClick={() => { setSecili(item); setView('duzenle'); }} />
                  <DeleteButton size="lg" onClick={() => setSilOnay(item.bankaTanimiId)} />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Desktop View */}
        <div className="hidden md:block overflow-x-auto leading-none">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50 text-[11px] font-black text-gray-400 uppercase tracking-widest">
                <th className="py-5 px-8 text-left">Banka / Şube Bilgisi</th>
                <th className="py-5 px-8 text-left">Ödeme Tipi</th>
                <th className="py-5 px-8 text-left">Hesap Sahibi</th>
                <th className="py-5 px-8 text-left">IBAN Numarası</th>
                <th className="py-5 px-8 text-right font-black">İşlemler</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtrelenmis.length === 0 ? (
                <EmptyState as="td" colSpan={5} icon="🏦" text="Eşleşen banka kaydı bulunamadı" />
              ) : filtrelenmis.map(item => (
                <tr key={item.bankaTanimiId} className="group hover:bg-sky-50/30 transition-colors duration-200">
                  <td className="py-6 px-8">
                    <div className="flex items-center gap-5">
                      <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-sky-500 to-indigo-700 flex items-center justify-center text-white text-base font-black shadow-xl shadow-sky-100 group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 tracking-tighter">
                        {item.ad?.[0]}
                      </div>
                      <div className="min-w-0">
                        <p className="font-black text-gray-800 tracking-tighter leading-none mb-2 uppercase text-base">{item.ad}</p>
                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-2">
                          <span className="w-1 h-1 bg-sky-400 rounded-full"></span> {item.sube || 'MERKEZ'} / {item.subeNo || '0000'}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="py-6 px-8">
                    <span className={`text-[10px] font-black px-4 py-1.5 rounded-full border tracking-widest shadow-sm ${item.odemeTip?.includes('KART') ? 'bg-indigo-50 text-indigo-700 border-indigo-100' :
                        item.odemeTip?.includes('NAKİT') ? 'bg-emerald-50 text-emerald-700 border-emerald-100' :
                          'bg-sky-50 text-sky-700 border-sky-100'
                      }`}>{item.odemeTip}</span>
                  </td>
                  <td className="py-6 px-8 font-black text-gray-700 uppercase tracking-tight text-xs overflow-hidden truncate max-w-[150px]">{item.hesapAdSoyad || '—'}</td>
                  <td className="py-6 px-8">
                    <div className="flex items-center gap-3 group/iban">
                      <span className="font-mono text-[10px] font-black tracking-widest bg-gray-50 text-gray-500 px-4 py-2 rounded-2xl border border-gray-100 shadow-inner group-hover/iban:border-sky-200 transition-all">{item.iban || '—'}</span>
                      {item.iban && (
                        <button onClick={() => kopyalaIban(item.iban)} className="w-8 h-8 flex items-center justify-center text-gray-300 hover:text-sky-600 transition-all hover:bg-sky-50 rounded-lg active:scale-90">
                          {kopyalandi === item.iban ? '✅' : '📋'}
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="py-6 px-8 text-right font-black">
                    <ActionButtons onEdit={() => { setSecili(item); setView('duzenle'); }} onDelete={() => setSilOnay(item.bankaTanimiId)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <UsageInfo
        color="sky"
        description="Banka Tanımları modülü, otelinizin tahsilat yaparken kullandığı banka hesaplarını, IBAN bilgilerini ve bu hesaplar üzerinden yapılan ödeme tiplerini merkezi olarak yönetmenizi sağlar."
        scenarios={[
          { title: "Havale ve EFT Takibi", content: "Rezervasyonlar için havale/EFT ödeme yöntemini seçtiğinizde, konfirme aşamasında misafirlerinize bu alanda tanımladığınız IBAN ve banka bilgilerini anlık olarak gönderebilirsiniz." },
          { title: "POS ve Kart Tahsilatı", content: "Kredi kartı tahsilatlarını yaptığınız banka hesaplarını 'KREDİ KARTI' ödeme tipi ile eşleştirerek, banka ekstreleriniz ile sistemdeki tahsilatları kolayca eşleştirebilirsiniz." },
          { title: "Kasa Mutabakatı", content: "Hesaplarınızı alt birimlere (TL, USD, EUR) göre ayırarak, her banka hesabındaki nakit akışını döviz bazında takip edebilir ve mutabakat sürecini hızlandırabilirsiniz." }
        ]}
      />

      {isOpen && createPortal(
        <BankaTanimiForm 
          kayit={secili} 
          odemeTipleri={odemeTipleri} 
          onKaydet={kaydet} 
          onGeri={() => { setView('liste'); setSecili(null); }} 
          kaydediliyor={kaydediliyor}
          hata={hata}
        />,
        document.body
      )}

      {silOnay && createPortal(
        <div className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center px-4 backdrop-blur-md animate-fade-in shadow-2xl">
          <div className="bg-white rounded-[2.5rem] shadow-2xl p-10 w-full max-sm:max-w-full max-w-sm text-center border border-gray-100">
            <div className="w-24 h-24 bg-rose-50 rounded-[2.5rem] flex items-center justify-center mx-auto mb-6 text-5xl shadow-xl shadow-rose-100/50 animate-bounce-subtle">🗑️</div>
            <h3 className="font-black text-gray-900 text-xl tracking-tight uppercase leading-none">Hesabı Sil</h3>
            <p className="text-[11px] font-black text-gray-500 mt-5 mb-10 leading-relaxed uppercase tracking-tight">
               <b className="text-gray-900">"{liste.find(x => x.bankaTanimiId === silOnay)?.ad}"</b> hesabı kalıcı olarak silinecek. Emin misiniz?
            </p>
            <div className="flex gap-4">
              <button onClick={() => setSilOnay(null)} className="flex-1 py-4 bg-gray-50 text-gray-600 rounded-2xl text-[10px] font-black hover:bg-gray-100 transition-all uppercase tracking-widest active:scale-95">İPTAL</button>
              <button onClick={() => handleSil(silOnay)} className="flex-1 py-4 bg-gradient-to-r from-rose-500 to-red-600 text-white rounded-2xl text-[10px] font-black shadow-xl shadow-rose-200 transition-all uppercase tracking-widest active:scale-95">EVET, SİL</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fadeIn { animation: fadeIn 0.4s ease-out forwards; }
        @keyframes scaleIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
        .animate-scale-in { animation: scaleIn 0.3s ease-out forwards; }
        @keyframes shake { 0%, 100% { transform: translateX(0); } 25% { transform: translateX(-5px); } 75% { transform: translateX(5px); } }
        .animate-shake { animation: shake 0.3s ease-in-out infinite; animation-iteration-count: 2; }
        @keyframes bounceSubtle { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
        .animate-bounce-subtle { animation: bounceSubtle 2s infinite; }
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
}